import { useQuery } from '@tanstack/react-query';

import { getWalkInStatus } from '@/services/api.client';
import { useVisibilityPolling } from '@/hooks/useVisibilityPolling';
import type { WalkInRequest } from '@/types';

const POLL_INTERVAL_MS = 5_000;

export interface WalkInStatusData {
  walkIn: WalkInRequest | null;
  isLoading: boolean;
  error: unknown;
}

/**
 * Polls walk-in approval status every 5s (faster than standard 30s).
 * Stops polling once the request reaches a terminal state (approved/denied/expired).
 */
export function useWalkInStatus(requestId: string | null): WalkInStatusData {
  const isTerminal = (data: WalkInRequest | undefined) =>
    data?.status === 'approved' || data?.status === 'denied' || data?.status === 'expired';

  const refetchInterval = useVisibilityPolling(POLL_INTERVAL_MS);

  const query = useQuery({
    queryKey: ['guard', 'walk-in-status', requestId],
    queryFn: () => getWalkInStatus(requestId ?? ''),
    enabled: !!requestId,
    refetchInterval: (query) => {
      if (isTerminal(query.state.data ?? undefined)) return false;
      return refetchInterval;
    },
    retry: 1,
  });

  return {
    walkIn: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}
